import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchRun, fetchLatestRuns, startRun, startBatch as startBatchRequest, errorMessageFrom
} from './api';

const TERMINAL = ['passed', 'failed', 'cancelled', 'timed_out', 'dispatch_failed'];
const PENDING = 'pending';
const POLL_FAST_MS = 5000;
const POLL_SLOW_MS = 15000;
const SLOW_AFTER_MS = 120000;
const CATALOG_REFRESH_MS = 30000;
// While a batch is draining, the next run only appears via the catalog refresh,
// so it has to keep pace with the per-run poll.
const BATCH_REFRESH_MS = 5000;

export const isTerminal = (status) => TERMINAL.includes(status);
export const isPending = (status) => status === PENDING;

// Runs are keyed by section so each row owns its state, and the whole set is
// rehydrated from the API — a run belongs to the server, not to this tab.
const useAutomationRun = () => {
  const [runs, setRuns] = useState({});
  const [error, setError] = useState(null);
  const [startingSectionId, setStartingSectionId] = useState(null);
  const [hydrating, setHydrating] = useState(true);
  const [starting, setStarting] = useState(false);

  const timers = useRef({});
  const polling = useRef(new Set());
  const startedAts = useRef({});
  const mounted = useRef(true);
  // Read synchronously after an await — state can lag a tick behind.
  const runsRef = useRef({});
  // Breaks the poll -> refreshLatest -> adopt -> poll callback cycle.
  const refreshLatestRef = useRef(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      Object.values(timers.current).forEach(clearTimeout);
      timers.current = {};
      polling.current.clear();
    };
  }, []);

  // runsRef is written before setRuns so the batch loop below never reads a
  // pre-dispatch (stale) run for a section it has just started.
  const upsert = useCallback((run) => {
    runsRef.current = { ...runsRef.current, [run.sectionId]: run };
    setRuns(runsRef.current);
  }, []);

  const poll = useCallback((run) => {
    polling.current.add(run.runId);

    const schedule = (current) => {
      const since = startedAts.current[current.runId] || Date.now();
      const delay = Date.now() - since > SLOW_AFTER_MS ? POLL_SLOW_MS : POLL_FAST_MS;

      timers.current[current.runId] = setTimeout(async () => {
        if (!mounted.current) return;
        try {
          const latest = await fetchRun(current.runId);
          if (!mounted.current) return;
          upsert(latest);
          if (!isTerminal(latest.status)) {
            schedule(latest);
            return;
          }
          polling.current.delete(latest.runId);
          // This read is what advanced the batch server-side, so claim the next
          // run now — the catalog refresh is paused while the tab is hidden.
          if (latest.batchId && refreshLatestRef.current) {
            refreshLatestRef.current().catch(() => {});
          }
        } catch (err) {
          polling.current.delete(current.runId);
          if (mounted.current) {
            setError(errorMessageFrom(err, 'Lost contact with the run. Refresh to try again.'));
          }
        }
      }, delay);
    };

    schedule(run);
  }, [upsert]);

  const adopt = useCallback((run) => {
    const held = runsRef.current[run.sectionId];
    // The catalog refresh can race a run we just started; never go backwards.
    if (held && held.runId !== run.runId
      && new Date(held.dispatchedAt).getTime() > new Date(run.dispatchedAt).getTime()) return;
    startedAts.current[run.runId] = new Date(run.dispatchedAt).getTime();
    upsert(run);
    // A pending run has nothing to report until the server dispatches it; the
    // catalog refresh is what picks that up.
    const live = !isTerminal(run.status) && !isPending(run.status);
    if (live && !polling.current.has(run.runId)) poll(run);
  }, [poll, upsert]);

  const refreshLatest = useCallback(async () => {
    const latest = await fetchLatestRuns();
    if (!mounted.current) return;
    latest.forEach(adopt);
  }, [adopt]);

  useEffect(() => {
    refreshLatestRef.current = refreshLatest;
  }, [refreshLatest]);

  useEffect(() => {
    (async () => {
      try {
        await refreshLatest();
      } catch (err) {
        if (mounted.current) {
          setError(errorMessageFrom(err, 'Could not load the run history.'));
        }
      } finally {
        if (mounted.current) setHydrating(false);
      }
    })();
  }, [refreshLatest]);

  // Picks up runs started by other admins or in another tab, and is also how a
  // draining batch's next run first shows up.
  const draining = Object.values(runs).some((run) => isPending(run.status));
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') refreshLatest().catch(() => {});
    }, draining ? BATCH_REFRESH_MS : CATALOG_REFRESH_MS);
    return () => clearInterval(id);
  }, [refreshLatest, draining]);

  const start = useCallback(async (sectionId) => {
    setError(null);
    setStartingSectionId(sectionId);
    try {
      const created = await startRun(sectionId);
      startedAts.current[created.runId] = Date.now();
      upsert(created);
      poll(created);
      return created;
    } catch (err) {
      // A 409 carries the run that blocked us, so a stale tab catches up.
      const conflict = err.response && err.response.status === 409 && err.response.data.run;
      if (conflict) adopt(conflict);
      setError(errorMessageFrom(err, 'Could not start the run.'));
      return null;
    } finally {
      if (mounted.current) setStartingSectionId(null);
    }
  }, [adopt, poll, upsert]);

  const startBatch = useCallback(async (sectionIds) => {
    if (!sectionIds || !sectionIds.length) return;
    setError(null);
    setStarting(true);
    try {
      const created = await startBatchRequest(sectionIds);
      created.forEach(adopt);
    } catch (err) {
      const conflict = err.response && err.response.status === 409 && err.response.data.run;
      if (conflict) adopt(conflict);
      setError(errorMessageFrom(err, 'Could not start the selected test cases.'));
    } finally {
      if (mounted.current) setStarting(false);
    }
  }, [adopt]);

  const liveRuns = Object.values(runs).filter((run) => !isTerminal(run.status));
  // Point at what is actually on the runner, not at something still queued.
  const activeRun = liveRuns.find((run) => !isPending(run.status)) || liveRuns[0] || null;
  const queuedRuns = liveRuns
    .filter((run) => isPending(run.status))
    .sort((a, b) => (a.batchPosition || 0) - (b.batchPosition || 0));
  const batchActive = liveRuns.some((run) => run.batchId);

  return {
    runs,
    activeRun,
    queuedRuns,
    error,
    startingSectionId,
    starting,
    hydrating,
    batchActive,
    isActive: !!activeRun,
    start,
    startBatch
  };
};

export default useAutomationRun;

